import {
    file,
    git,
    json,
    pkg,
    shell,
    Task,
    TaskCategory,
    tsconfig,
} from "@buurman/sdk";

interface Options {
    [name: string]: any;
}

const extractExtensionsFromPrettierCommand = (command: string) => {
    const regex = /["'][^"']+\.\{?([^"'\}]+)\}?["']/g;
    const extensions = [];
    let match: RegExpExecArray | null;

    regex.lastIndex = 0;

    while ((match = regex.exec(command))) {
        extensions.push(...match[1].split(","));
    }

    return extensions;
};

const task: Task<Options> = {
    name: "prettier",
    //npmPackage: require("../package.json").name,
    npmPackage: "@buurman-tasks/prettier",
    description: "Adds prettier to the project.",
    category: TaskCategory.TOOLING,
    tags: [
        "prettier",
        "formatting",
        "javascript",
        "typescript",
        "css",
        "scss",
        "yaml",
        "json",
        "markdown",
    ],
    options: {},
    apply: async () => {
        const shouldCommit = await git().hasCleanWorkingCopy();
        const currentFormatScript = await pkg().getScript("format");
        const react = await pkg().hasDependency("react");
        const createReactApp = await pkg().hasDependency("react-scripts");
        const nodeSass = await pkg().hasDependency("node-sass");
        const typescript = await tsconfig().exists();
        const typescriptOutDir =
            typescript &&
            (await tsconfig().emitsCode()) &&
            (await tsconfig().getOutDir());

        const extensions = currentFormatScript
            ? extractExtensionsFromPrettierCommand(currentFormatScript)
            : ([
                  ...(typescript ? ["ts", react && "tsx"] : []),
                  react && "jsx",
                  createReactApp && "css",
                  nodeSass && "scss",
                  "js",
                  "md",
                  "json",
                  "yml",
                  "yaml",
              ].filter(Boolean) as string[]);

        // it is important double quotes (and not single quotes) are used
        // see https://github.com/prettier/prettier/issues/4086#issuecomment-370228517
        const glob = `"**/*.{${extensions.join(",")}}"`;

        // TODO find build directory using tsconfig,
        //  .gitignore, intellij configuration (excluded dir),
        //  rollup config, webpack config, detect create-react-app

        await file(".prettierignore")
            .appendLines(
                [
                    "package-lock.json",
                    "node_modules/",
                    createReactApp && "build",
                    typescriptOutDir,
                ].filter(Boolean) as string[],
            )
            .save();

        await json(".prettierrc.json")
            .transform(config => ({
                tabWidth: 4,
                endOfLine: "lf",
                trailingComma: "all",
                arrowParens: "avoid",
                ...(config || {}),
            }))
            .save();

        await shell().exec("npm", ["i", "-D", "prettier"]);

        await pkg()
            .setScript("format", `prettier --loglevel warn --write ${glob}`)
            .save();

        if (shouldCommit) {
            await git()
                .add(".")
                .commit("chore(prettier): add prettier")
                .execute();
        }

        await pkg().runScript("format");

        if (shouldCommit) {
            await git()
                .add(".")
                .commit("chore(prettier): format all files")
                .execute();
        }
    },
    isInstalled: async () =>
        (await pkg().hasDevDependency("prettier")) &&
        (await pkg().hasScript("format")) &&
        (await json(".prettierrc.json").exists()) &&
        (await file(".prettierignore").exists()),
    isSupported: () => pkg().exists(),
};

export default task;
